const appConfig = JSON.parse(import.meta.env.VITE_APP_CONFIG);

import "@zanmato/vue3-treeselect/dist/vue3-treeselect.min.css";
import "vue-toastification/dist/index.css";
// import styles
import "./assets/styles.css";

import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import Toast, { useToast } from "vue-toastification";
import {
    auth,
    callable,
    db
} from "./db";
// router
import {
    createRouter,
    createWebHistory
} from "vue-router";
import { failAuthentication, getTenantClaim } from "./authState.js";

import App from './App.vue'
// icons
import FontAwesomeIcon from "./icons.js";
// import components
import Treeselect from "@zanmato/vue3-treeselect";
// import v-tooltip
import VTooltipPlugin from 'v-tooltip'
import {
    createApp
} from 'vue'
// error reporter
import { installErrorReporter } from "./errorReporter.js";
// import vue-advanced-chat
// import { register } from 'vue-advanced-chat';
// dynamically import routes from the defined config
import routes from "./views/routes";
// store / vuex
import store from "./store.js";

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });




const router = createRouter({
    "history": createWebHistory(),
    "routes": routes
});

const toast = useToast();

const w = t => {
    return new Promise((res, rej) => setTimeout(res, t || 1000));
}

const max_attempts = 10;

// This is an asynchronous function called routeGuard which takes in two parameters, to and from, and returns either true or false.
async function runRouteGuard(to, from, attempt) {
    // Open / external-self routes (clean no-nav layout for legacy open-forms and new self-service externals)
    // are allowed even without full internal Security.General.User. Ownership/claim checks happen in
    // per-route meta.lock (or in the component after claims are available).
    if (to.meta.open === true) return;
    if (to.meta.customerPortal === true) {
        // Customer portal routes: require tenant claim with customer=true
        // Migration window: getTenantClaim handles both nested and flat structures
        const user = auth.currentUser;
        if (user) {
            try {
                const idTokenResult = await user.getIdTokenResult();
                const claims = idTokenResult?.claims || {};
                const tenantKey = appConfig.appPrefix;
                const tenantClaim = getTenantClaim(claims, tenantKey);

                if (tenantClaim?.customer === true) {
                    // Has customer access on this tenant
                    if (store.state.user?.CustomerID) return true;
                }
            } catch (e) {
                console.error("Failed to check customer portal claims:", e);
            }
        }

        if (store.state.failedUser || attempt >= max_attempts) return false;
        await w(200);
        return runRouteGuard(to, from, attempt + 1);
    }

    // If the "to" route has a defined "storeData" meta property, then loop through each element of the "storeData" array and call the "store" method on the "db" object with the parameters: dataToStore and store.
    if (to?.meta?.storeData) {
        for (let dataToStore of to.meta.storeData) {
            try {
                await db.store(dataToStore, store);
            } catch (error) {
                continue;
            }
        }
    }

    // External self routes (FTC/Signatory self-service) skip Security.General.User check
    // but MUST still run their lock() functions to verify tenant claims


    if (store.state.failedUser) return false;

    if (attempt >= max_attempts) {
        await failAuthentication({
            store,
            toast,
            message: "Sign in could not be completed. Please try again.",
        });
        return false;
    }

    // Internal routes require BOTH tenant class 'internal' AND Security.General.User
    // External self routes skip this check (they have their own lock() functions)

    const user = auth.currentUser;
    if (user) {
        try {
            const idTokenResult = await user.getIdTokenResult();
            const claims = idTokenResult?.claims || {};
            const tenantKey = appConfig.appPrefix;
            const tenantClaim = getTenantClaim(claims, tenantKey);

            // Check if user has internal class on this tenant
            if (!tenantClaim) {
                return false;
            }
            // if (!tenantClaim.internal && to.path !== "/") {
            //     await failAuthentication({
            //         store,
            //         toast,
            //         message: "Your account does not have permission to access this part of the application.",
            //         severity: "warning",
            //         signOut: false,
            //     });
            //     return false;
            // }

            if (!tenantClaim.internal) return true;
        } catch (e) {
            console.error("Failed to check tenant claims:", e);
            // Fall through to Security check below
        }
    }

    // If the "store" object does not have a defined "state" property, or if the "state" property does not have a defined "user" property, or if the "user" property does not have a defined "Security" property, then wait for 3 seconds.
    if (!store?.state?.user?.Security) {
        const sleep = (milliseconds) =>
            new Promise((resolve) => setTimeout(resolve, milliseconds));
        await sleep(200);
        return runRouteGuard(to, from, attempt + 1);
    }

    if (!store.state.user.Security.General.User) {
        return false;
    }


    // If the "to" route has a defined "lock" meta property, then call the lock function with the parameters: store.state.user, { to, from, store, router, db }
    if (to?.meta?.lock) {
        const enter = await to?.meta?.lock(store.state.user, { to, from, store, router, db });

        // If the first element of the "enter" array is equal to true, then show an error message in a toast and return false
        if (enter[0] === true) {
            toast.error(
                "Navigation blocked, you may not have the permissions to view this page."
            );
            return false;
        };
    }



    // Return true if all conditions have passed
    return true;
}

async function routeGuard(to, from) {
    return runRouteGuard(to, from, 0);
}

router.beforeEach(routeGuard);

// Register vue-advanced-chat web component
// register();

const app = createApp(App)
app.use(VTooltipPlugin);
app.use(router);
app.use(store);
app.use(Toast, {
    "transition": "Vue-Toastification__fade",
    "maxToasts": 20,
    "newestOnTop": false,
    "position": "bottom-right",
    "timeout": 3000,
    "closeOnClick": true,
    "pauseOnFocusLoss": true,
    "pauseOnHover": true,
    "draggable": false,
    "draggablePercent": 0.6,
    "showCloseButtonOnHover": true,
    "hideProgressBar": true,
    "closeButton": false,
    "icon": true,
    "rtl": false
});

// Install error reporter early (before mount)
// TEST tenant only - appPrefix gate enforced in errorReporter.js
installErrorReporter(callable, {
    databaseURL: appConfig?.firebaseConfig?.databaseURL,
    app: "database-frontend",
    release: "0.0.0", // TODO: derive from build metadata
    appPrefix: appConfig?.appPrefix,
}, app);

app
    .component("font-awesome-icon", FontAwesomeIcon)
    .component("treeselect", Treeselect)
    .mount('#app')

